
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { updateUserProfile } from "@/services/auth/userProfileService";
import { CNPJInput, PhoneInput } from "@/components/ui/masked-inputs";
import { LogoUploader } from "@/components/ui/logo-uploader/LogoUploader";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const formSchema = z.object({
  document: z.string().regex(/^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/, {
    message: "CNPJ deve estar no formato XX.XXX.XXX/XXXX-XX"
  }),
  phone: z.string().regex(/^\(\d{2}\) \d{5}\-\d{4}$/, {
    message: "Telefone deve estar no formato (XX) XXXXX-XXXX"
  }),
  address: z.string().min(5, "Endereço deve ter pelo menos 5 caracteres"),
  website: z.string().url("URL do website inválida").optional().or(z.string().length(0))
});

type FormValues = z.infer<typeof formSchema>;

export function CompleteProfileForm() {
  const { user, updateUserInContext } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(user?.profileImage || null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      document: user?.document || '',
      phone: user?.phone || '',
      address: user?.address || '',
      website: user?.website || ''
    }
  });

  useEffect(() => {
    if (user) {
      form.reset({
        document: user.document || '',
        phone: user.phone || '',
        address: user.address || '',
        website: user.website || ''
      });
      setProfileImageUrl(user.profileImage || null);
    }
  }, [user, form]);

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setProfileImageUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (!user) {
      setError("Você precisa estar logado para completar seu perfil");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Send profile data to Supabase
      const updatedUser = await updateUserProfile(user.id, {
        document: values.document,
        phone: values.phone,
        address: values.address,
        website: values.website,
        profileImage: profileImage || undefined
      });

      // Update local context if successful
      if (updatedUser && updateUserInContext) {
        updateUserInContext(updatedUser);
      }

      toast({
        title: "Perfil atualizado com sucesso",
        description: "Redirecionando para o dashboard..."
      });

      // Redirect to dashboard
      setTimeout(() => {
        navigate("/dashboard");
      }, 1000);
    } catch (err: any) {
      console.error("Error updating profile:", err);
      setError(err.message || "Erro ao atualizar o perfil");
      
      toast({
        title: "Erro",
        description: err.message || "Não foi possível atualizar seu perfil",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const userInitials = user?.name ? user.name.substring(0, 2).toUpperCase() : "";

  return (
    <Form {...form}>
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex flex-col items-center mb-6">
          <FormLabel className="mb-2">Foto de Perfil</FormLabel>
          <LogoUploader 
            logoUrl={profileImageUrl || ""}
            fallback={userInitials}
            handleLogoChange={handleProfileImageChange}
            isLoading={isSubmitting}
            variant="avatar"
            size="lg"
            label="Foto"
          />
        </div>

        <FormField
          control={form.control}
          name="document"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CNPJ</FormLabel>
              <FormControl>
                <CNPJInput
                  {...field}
                  disabled={isSubmitting}
                  className="w-full"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Telefone</FormLabel>
              <FormControl>
                <PhoneInput
                  {...field}
                  disabled={isSubmitting}
                  className="w-full"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Endereço</FormLabel>
              <FormControl>
                <Textarea 
                  {...field}
                  disabled={isSubmitting}
                  placeholder="Endereço completo da empresa"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website (opcional)</FormLabel>
              <FormControl>
                <Input 
                  {...field}
                  disabled={isSubmitting}
                  placeholder="https://seusite.com.br"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isSubmitting}
        >
          {isSubmitting ? "Salvando..." : "Salvar e Continuar"}
        </Button>
      </form>
    </Form>
  );
}
